'use client';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from '@ohif/ui-next';
import PropTypes from 'prop-types';
import { Header, Onboarding, useModal } from '@ohif/ui-next';
import { useAppConfig } from '@state';
import { useTranslation } from 'react-i18next';
import { SidebarNavigation } from '@ohif/ui';
import { getHeaderConfig } from '../Users/Users'; // Asumo la reutilización

import {
  fetchSettings,
  updateSettings,
  SettingsAPI,
  SettingsData,
} from './settings';

// --- Estado Inicial ---
const INITIAL_SETTINGS_DATA: SettingsData = {
  institution_name: '',
  logo_primary_base64: null,
  logo_secondary_base64: null,
  primary_logo_filename: null,
  secondary_logo_filename: null,
};

function SettingsPanel({ servicesManager }) {
  const [settings, setSettings] = useState<SettingsData>(INITIAL_SETTINGS_DATA);
  const [isSaving, setIsSaving] = useState(false);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { show } = useModal();
  const [appConfig] = useAppConfig();
  const { menuOptions } = getHeaderConfig({ servicesManager, t, show, navigate, appConfig });

  // Función auxiliar para leer archivo y convertir a Base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  // Cargar configuración existente
  const loadSettings = async () => {
    try {
      const apiSettings: SettingsAPI = await fetchSettings();
      setSettings(apiSettings);
    } catch (error) {
      toast.error('Error al cargar la configuración: ' + (error as any).message);
    }
  };

  useEffect(() => {
    // Aquí también iría tu chequeo de token/permisos de administrador
    loadSettings();
  }, []);

  // Manejador de subida de archivo
  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>, logoKey: 'logo_primary_base64' | 'logo_secondary_base64', filenameKey: 'primary_logo_filename' | 'secondary_logo_filename') => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      if (file.size > 5000000) { // Límite de 5 MB
        toast.warning('El archivo es demasiado grande. Máximo 5 MB.');
        return;
      }

      const base64String = await fileToBase64(file);

      setSettings(prev => ({
        ...prev,
        [logoKey]: base64String,
        [filenameKey]: file.name,
      }));
      toast.success(`Logo (${file.name}) cargado y listo para guardar.`);
    } catch (error) {
      toast.error('Error al procesar el archivo.');
    }
  };

  const handleRemoveLogo = (logoKey: 'logo_primary_base64' | 'logo_secondary_base64', filenameKey: 'primary_logo_filename' | 'secondary_logo_filename') => {
    setSettings(prev => ({
      ...prev,
      [logoKey]: null,
      [filenameKey]: null,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const response = await updateSettings(settings);
      setSettings(response);
      toast.success('Configuración guardada exitosamente.');
    } catch (err: any) {
      toast.error('Error al guardar: ' + (err.message || err));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex h-full flex-col bg-black">
      <Header
        isSticky
        menuOptions={menuOptions}
        isReturnEnabled={false}
        WhiteLabeling={appConfig.whiteLabeling}
      />
      <Onboarding />
      <SidebarNavigation onFilterToggle={() => navigate('/')} />

      <div className="container mx-auto min-h-screen bg-gray-50 pt-5 text-white max-sm:p-4 sm:pl-9">
        <h1 className="text-3xl font-bold mb-4">Configuración General de la Institución</h1>
        <p className="mb-6 text-gray-400">Administra el nombre y los logotipos de la aplicación.</p>

        <div className="bg-secondary-main rounded shadow p-6 max-w-3xl">
          <form onSubmit={handleSubmit} className="space-y-6">

            {/* Nombre de la Institución */}
            <div>
              <label className="block text-sm font-medium mb-1">Nombre de la Institución</label>
              <input
                type="text"
                name="institution_name"
                value={settings.institution_name || ''}
                onChange={(e) => setSettings({ ...settings, institution_name: e.target.value })}
                className="focus:ring-primary-main w-full rounded border bg-black px-3 py-2 focus:outline-none focus:ring"
                required
              />
            </div>

            {/* Contenedor de Logos */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

              {/* Logo Primario */}
              <LogoUploader
                label="Logo principal (1000 x 400)"
                logoKey="logo_primary_base64"
                filenameKey="primary_logo_filename"
                settings={settings}
                handleLogoUpload={handleLogoUpload}
                handleRemoveLogo={handleRemoveLogo}
              />

              {/* Logo Secundario */}
              <LogoUploader
                label="Logo de carga"
                logoKey="logo_secondary_base64"
                filenameKey="secondary_logo_filename"
                settings={settings}
                handleLogoUpload={handleLogoUpload}
                handleRemoveLogo={handleRemoveLogo}
              />

            </div>

            <div className="pt-4 border-t border-gray-700 flex justify-end">
              <button
                type="submit"
                className="rounded bg-blue-600 px-6 py-2 text-white hover:bg-blue-900 transition duration-200"
                disabled={isSaving}
              >
                {isSaving ? 'Guardando...' : 'Guardar Configuración'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// Componente para manejar la subida de un solo logo
const LogoUploader = ({ label, logoKey, filenameKey, settings, handleLogoUpload, handleRemoveLogo }) => {
  const logoBase64 = settings[logoKey];
  const filename = settings[filenameKey];

  return (
    <div className="space-y-3">
      <label className="block text-md font-semibold">{label}</label>

      {logoBase64 ? (
        // Vista Previa y Botón de Eliminar
        <div className="border border-gray-600 rounded p-3 space-y-2">
          <p className="text-sm text-gray-300">Archivo: {filename}</p>
          <img src={logoBase64} alt="Vista Previa" className="h-16 max-w-full object-contain mx-auto" />
          <button
            type="button"
            onClick={() => handleRemoveLogo(logoKey, filenameKey)}
            className="w-full text-red-400 text-sm hover:text-red-600 mt-2"
          >
            Eliminar Logo
          </button>
        </div>
      ) : (
        // Selector de Archivo
        <label className="block w-full cursor-pointer rounded border border-dashed border-gray-500 bg-black/50 p-4 text-center hover:border-blue-500 transition duration-200">
          <p className="text-sm text-gray-400">Haz click para subir logo (PNG/JPG)</p>
          <input
            type="file"
            accept="image/png, image/jpeg, image/svg+xml"
            className="hidden"
            onChange={(e) => handleLogoUpload(e, logoKey, filenameKey)}
          />
        </label>
      )}
    </div>
  );
};

SettingsPanel.propTypes = {
  servicesManager: PropTypes.object.isRequired,
};

export default SettingsPanel;
